import { Response } from 'express'
import { IPage, IGatedContent, CONTENT_TYPE } from 'marketing-site/@types/generated/contentful'
import getPageByPath from 'marketing-site/lib/getPageByPath'
import { IWebinar } from 'marketing-site/src/components/bespoke/webinars'
import EntryMarker from 'marketing-site/src/components/common/EntryMarker'
import Page from 'marketing-site/src/components/common/Page'
import { IAssignedVariations } from 'marketing-site/src/components/context/AssignedVariationsContext'
import { GetServerSideProps } from 'next'
import React from 'react'
import { getServerSideProps as getErrorServerSideProps } from '../_error'
import { WebinarSeries } from '../../src/library/components/WebinarSeries'
import { WebinarSeriesHero } from '../../src/library/components/WebinarSeriesHero'
import { IProps as IWebinarSeries } from '../../src/library/components/WebinarSeries'
import { IProps as IWebinarSeriesHero } from '../../src/library/components/WebinarSeriesHero'
import { renderTransformedComponent } from 'marketing-site/lib/transformedComponents'
import { useBrand } from 'marketing-site/src/library/providers/brandProvider'
import { Environment, getEnvironment } from 'marketing-site/lib/environment'
import { transformImage } from 'marketing-site/src/transformers/elements/ContentfulImage'
import buildPath from 'marketing-site/lib/buildPath'
import useLocaleCode from 'marketing-site/src/library/hooks/useLocaleCode'

interface IProps {
  page: IPage
  webinars?: IWebinar[]
  webinarSeries?: IWebinarSeries
  assignedVariations: IAssignedVariations
  hero?: IWebinarSeriesHero
  gatedContent?: IGatedContent | null
}

export default function WebinarSeriesPage({
  page,
  webinars,
  webinarSeries,
  hero,
  gatedContent,
}: IProps): React.ReactElement {
  const brand = useBrand()
  const localeCode = useLocaleCode()

  if (!webinars || !webinarSeries) {
    return (
      <EntryMarker entry={page}>
        <Page page={page} />
      </EntryMarker>
    )
  }

  const seo = {
    metaTitle: webinarSeries.name,
    metaDescription: webinarSeries.description || '',
    openGraphTitle: webinarSeries.name,
    openGraphDescription: webinarSeries.description,
    twitterImage: '',
    disableCrawling: false,
    metaCanonical: buildPath({
      localeCode,
      pathname: `/webinar-series/${webinarSeries.slug}`,
    }).canonical,
    openGraphImage: '',
  }

  return (
    <EntryMarker entry={page}>
      <Page page={page} title={webinarSeries.name} seo={seo}>
        {hero && <WebinarSeriesHero {...hero} />}
        <WebinarSeries
          name={webinarSeries.name}
          slug={webinarSeries.slug}
          webinars={webinars}
          image={webinarSeries.image}
        />
        {gatedContent &&
          renderTransformedComponent(
            'gatedContent' as CONTENT_TYPE,
            gatedContent,
            undefined,
            brand,
          )}
      </Page>
    </EntryMarker>
  )
}

export const getServerSideProps: GetServerSideProps<IProps> = async function ({
  req,
  res,
  query,
  resolvedUrl,
}) {
  const page = await getPageByPath('webinars')
  if (!page) {
    res.statusCode = 404
    return getErrorServerSideProps({ req, res, query: {}, resolvedUrl })
  }

  const { getAssignedVariations } = await import('marketing-site/lib/abTests')

  const assignedVariations = await getAssignedVariations(req, page)

  const { getWebinarSeries } = await import('marketing-site/lib/webinar')
  const { transformWebinar } = await import('marketing-site/src/transformers/Webinar')
  const { transformWebinarSeriesHero } = await import(
    'marketing-site/src/transformers/components/ContentfulWebinarSeriesHero'
  )
  const { captureException } = await import('marketing-site/lib/sentry')
  const seriesData = await getWebinarSeries((query.webinarSeriesSlug as string).toLowerCase())

  if (!seriesData) {
    ;(res as Response).redirect('/webinars')
    // we need early return here to prevent any errors downstream
    return getErrorServerSideProps({ req, res, query: {}, resolvedUrl })
  }

  const enrichedWebinars: IWebinar[] = []

  const webinarSeries = seriesData.fields
  await Promise.all(
    seriesData.fields.webinars.map(async (webinar) => {
      try {
        return enrichedWebinars.push(await transformWebinar(webinar))
      } catch (err) {
        if (getEnvironment() === Environment.Production) {
          captureException(err)
        }
        return null
      }
    }),
  )

  const webinarParsedSeries = {
    ...webinarSeries,
    webinars: enrichedWebinars,
    image: transformImage(webinarSeries.image),
  }

  const hero = transformWebinarSeriesHero(seriesData)

  return {
    props: {
      page,
      hero,
      assignedVariations,
      webinarSeries: webinarParsedSeries,
      webinars: enrichedWebinars,
      gatedContent: webinarSeries.gatedContent || null,
    },
  }
}
